import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useStore from '../store/useStore';
import useAssistant from '../store/useAssistant';
import AiMark from './assistant/AiMark';
import CompanySelector from './CompanySelector';
import DashboardSelector from './DashboardSelector';
import { useDashboardStatus } from '../hooks/useDashboardStale';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useStore((state) => state.user);
  const logout = useStore((state) => state.logout);
  const wsConnected = useStore((state) => state.wsConnected);
  const currentDashboard = useStore((state) => state.currentDashboard);
  const dashboardLastUpdates = useStore((state) => state.dashboardLastUpdates);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const wallMode = useStore((state) => state.wallMode);
  const alertCount = useStore((state) => state.alerts.length);
  const dashboardStatus = useDashboardStatus();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNav = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const getDashboardLastUpdate = () => {
    if (!currentDashboard?.id) return null;
    return dashboardLastUpdates[currentDashboard.id];
  };

  const getConnectionStatus = () => dashboardStatus;

  const formatLastUpdate = () => {
    const lastUpdate = getDashboardLastUpdate();
    if (!lastUpdate) return 'Never';
    try {
      const date = new Date(lastUpdate);
      return date.toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch {
      return 'Unknown';
    }
  };

  const isActive = (path) => location.pathname === path;
  const status = getConnectionStatus();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const assistantAvailable = useAssistant((s) => Boolean(s.status?.available));
  const assistantOpen = useAssistant((s) => s.open);
  const toggleAssistant = useAssistant((s) => s.togglePanel);
  const isSuperAdmin = user?.role === 'super_admin';

  if (isFullscreen || wallMode) return null;

  const startsWith = (prefix) => location.pathname.startsWith(prefix);
  const ICON = {
    dashboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    reports: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>,
    kpis: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
    alerts: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
    sources: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 4h16v4H4zM4 10h16v4H4zM4 16h16v4H4z"/></svg>,
    keys: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78zM15.5 7.5l3 3L22 7l-3-3"/></svg>,
    users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    companies: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 21h18M3 7v14M21 7v14M6 11h4M6 15h4M14 11h4M14 15h4M9 21V3h6v18"/></svg>,
    settings: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>,
    profile: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  };
  // Pages grouped by what you are doing: looking at results, managing the
  // data that feeds them, administering people and access, your own account.
  const navGroups = [
    { label: null, items: [
      { path: '/', label: 'Dashboard', icon: ICON.dashboard, active: isActive('/') },
    ] },
    { label: 'Reporting', items: [
      { path: '/reports', label: 'Reports', icon: ICON.reports, active: startsWith('/reports') || startsWith('/statements') },
      ...(assistantAvailable ? [{ path: '#assistant', label: 'AI assistant', icon: <AiMark size={20} />, active: assistantOpen, ai: true, onClick: () => { toggleAssistant(); setIsMobileOpen(false); } }] : []),
      ...(isAdmin ? [{ path: '/admin/kpis', label: 'KPIs', icon: ICON.kpis, active: startsWith('/admin/kpis') || startsWith('/kpis') }] : []),
      { path: '/alerts', label: 'Alerts', icon: ICON.alerts, active: isActive('/alerts'),
        badge: alertCount > 0 ? <span className="sidebar-alert-count" aria-label={`${alertCount} active alerts`}>{alertCount}</span> : null },
    ] },
    ...(isAdmin ? [{ label: 'Data', items: [
      { path: '/admin/data-sources', label: 'Data sources', icon: ICON.sources, active: startsWith('/admin/data-sources') },
      { path: '/admin/api-keys', label: 'API keys', icon: ICON.keys, active: isActive('/admin/api-keys') },
    ] }] : []),
    ...(isAdmin ? [{ label: 'Administration', items: [
      { path: '/admin/users', label: 'Users', icon: ICON.users, active: isActive('/admin/users') },
      ...(isSuperAdmin ? [{ path: '/admin/companies', label: 'Companies', icon: ICON.companies, active: isActive('/admin/companies') }] : []),
    ] }] : []),
    { label: 'Account', items: [
      { path: '/settings', label: 'Settings', icon: ICON.settings, active: isActive('/settings') },
      { path: '/profile', label: 'Profile', icon: ICON.profile, active: isActive('/profile') },
    ] },
  ];

  return (
    <>
      {/* Mobile hamburger toggle */}
      <button
        className="sidebar-mobile-toggle"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Toggle navigation"
      >
        {isMobileOpen ? '\u2715' : '\u2630'}
      </button>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div className="sidebar-overlay" onClick={() => setIsMobileOpen(false)} />
      )}

      <aside className={`sidebar ${isMobileOpen ? 'sidebar-open' : ''}`}>
        {/* Brand */}
        <div className="sidebar-brand" onClick={() => handleNav('/')}>
          <img src="/kinspeed-logo.png" alt="Kinspeed" className="sidebar-logo" />
          <span className="sidebar-brand-text">Kinspeed Wallboard</span>
        </div>

        {/* Selectors */}
        <div className="sidebar-selectors">
          <div className="sidebar-selector-group">
            <label className="sidebar-selector-label">Company</label>
            <CompanySelector />
          </div>
        </div>

        {/* Navigation, grouped by purpose */}
        <nav className="sidebar-nav" aria-label="Main">
          {navGroups.map((group) => (
            <div key={group.label || 'main'} className="sidebar-nav-group" role="group" aria-label={group.label || 'Dashboards'}>
              {group.label && <span className="sidebar-nav-section">{group.label}</span>}
              {group.items.map((item) => (
                <button
                  key={item.path}
                  className={`sidebar-nav-item ${item.active ? 'active' : ''} ${item.ai ? 'ai-nav' : ''}`}
                  onClick={() => (item.onClick ? item.onClick() : handleNav(item.path))}
                  aria-current={item.active && !item.onClick ? 'page' : undefined}
                  aria-pressed={item.onClick ? item.active : undefined}
                >
                  {item.icon}
                  <span className={item.ai ? 'ai-nav-label' : undefined}>{item.label}</span>
                  {item.badge}
                </button>
              ))}
              {!group.label && (
                <div className="sidebar-dashboard-choice">
                  <DashboardSelector onChosen={() => setIsMobileOpen(false)} />
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Status */}
        <div className="sidebar-status">
          <div className="sidebar-status-row">
            <span className="sidebar-status-label">Status</span>
            <div className="sidebar-connection">
              <div className={`sidebar-status-dot ${status.className}`} />
              <span>{status.label}</span>
            </div>
          </div>
          <div className="sidebar-status-row">
            <span className="sidebar-status-label">Updated</span>
            <span className="sidebar-status-value">{formatLastUpdate()}</span>
          </div>
        </div>

        {/* User + Logout */}
        <div className="sidebar-footer">
          {isSuperAdmin && <span className="sidebar-role-badge super-admin">Super Admin</span>}
          {user?.role === 'admin' && <span className="sidebar-role-badge admin">Admin</span>}
          <div className="sidebar-user">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="sidebar-user-icon"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <span className="sidebar-username">{user?.full_name || user?.username}</span>
          </div>
          <button onClick={handleLogout} className="sidebar-logout">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
